import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useToast } from '../context/ToastContext';
import axios from 'axios';
import { Copy, Plus, Edit2, Trash2, RefreshCw, X, Check, Clock, Users, Filter, ChevronDown, ChevronUp, Rss, ExternalLink } from 'lucide-react';

interface CalendarFeedData {
  id: number;
  user_id: number;
  token: string;
  name: string | null;
  team_id: number | null;
  only_lineup_matches: boolean;
  last_fetched_at: string | null;
  fetch_count: number;
  created_at: string;
  updated_at: string;
}

interface CalendarFeedFormData {
  name: string;
  team_id: number | 'all';
  only_lineup_matches: boolean;
}

const CalendarFeed: React.FC = () => {
  const { user } = useAuth();
  const { teams } = useTeam();
  const toast = useToast();
  const [feeds, setFeeds] = useState<CalendarFeedData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFeed, setEditingFeed] = useState<CalendarFeedData | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [copiedFeedId, setCopiedFeedId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CalendarFeedFormData>({
    name: '',
    team_id: 'all',
    only_lineup_matches: false
  });

  const fetchFeeds = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/calendar/feeds');
      setFeeds(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar feeds:', error);
      toast.error('Failed to load calendar feeds.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (user) {
      fetchFeeds();
    }
  }, [user, fetchFeeds]);

  const getFeedUrl = (token: string): string => {
    // Use the same base URL as axios (backend API URL)
    // axios.defaults.baseURL is like 'http://tennis-local.henkhaus.org:8000/api'
    const apiBaseUrl = axios.defaults.baseURL || 'http://tennis-local.henkhaus.org:8000/api';
    // Remove trailing /api if present, then add the calendar feed path
    const baseUrl = apiBaseUrl.replace(/\/api$/, '');
    return `${baseUrl}/api/calendar/feed/${token}.ics`;
  };

  const copyToClipboard = async (text: string, feedId: number) => {
    try {
      // Try modern Clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts (HTTP)
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          const successful = document.execCommand('copy');
          if (!successful) throw new Error('execCommand copy failed');
        } finally {
          document.body.removeChild(textArea);
        }
      }
      setCopiedFeedId(feedId);
      toast.success('Calendar feed URL copied to clipboard!');
      setTimeout(() => setCopiedFeedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy URL. Please select and copy it manually.');
    }
  };

  const handleCreateFeed = async () => {
    try {
      const payload = {
        name: formData.name || null,
        team_id: formData.team_id === 'all' ? null : formData.team_id,
        only_lineup_matches: formData.only_lineup_matches
      };
      
      await axios.post('/calendar/feeds', payload);
      toast.success('Calendar feed created successfully!');
      setShowCreateModal(false);
      setFormData({ name: '', team_id: 'all', only_lineup_matches: false });
      fetchFeeds();
    } catch (error: any) {
      console.error('Failed to create calendar feed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create calendar feed.');
    }
  };

  const handleUpdateFeed = async () => {
    if (!editingFeed) return;
    
    try {
      const payload: any = {};
      if (formData.name !== editingFeed.name) {
        payload.name = formData.name || null;
      }
      if (formData.team_id !== (editingFeed.team_id || 'all')) {
        payload.team_id = formData.team_id === 'all' ? null : formData.team_id;
      }
      if (formData.only_lineup_matches !== editingFeed.only_lineup_matches) {
        payload.only_lineup_matches = formData.only_lineup_matches;
      }
      
      await axios.put(`/calendar/feeds/${editingFeed.id}`, payload);
      toast.success('Calendar feed updated successfully!');
      setEditingFeed(null);
      setFormData({ name: '', team_id: 'all', only_lineup_matches: false });
      fetchFeeds();
    } catch (error: any) {
      console.error('Failed to update calendar feed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update calendar feed.');
    }
  };

  const handleDeleteFeed = async (feedId: number) => {
    if (!window.confirm('Are you sure you want to delete this calendar feed? This will break any existing calendar subscriptions.')) {
      return;
    }
    
    try {
      await axios.delete(`/calendar/feeds/${feedId}`);
      toast.success('Calendar feed deleted successfully!');
      fetchFeeds();
    } catch (error: any) {
      console.error('Failed to delete calendar feed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete calendar feed.');
    }
  };

  const handleRegenerateToken = async (feedId: number) => {
    if (!window.confirm('Regenerating the token will break any existing calendar subscriptions. You will need to re-subscribe with the new URL. Continue?')) {
      return;
    }
    
    try {
      await axios.post(`/calendar/feeds/${feedId}/regenerate-token`);
      toast.success('Token regenerated successfully! Please update your calendar subscription with the new URL.');
      fetchFeeds();
    } catch (error: any) {
      console.error('Failed to regenerate token:', error);
      toast.error(error.response?.data?.detail || 'Failed to regenerate token.');
    }
  };

  const openEditModal = (feed: CalendarFeedData) => {
    setEditingFeed(feed);
    setFormData({
      name: feed.name || '',
      team_id: feed.team_id || 'all',
      only_lineup_matches: feed.only_lineup_matches
    });
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setEditingFeed(null);
    setFormData({ name: '', team_id: 'all', only_lineup_matches: false });
  };

  const getFeedDisplayName = (feed: CalendarFeedData): string => {
    if (feed.name) return feed.name;
    
    const parts: string[] = [];
    if (feed.team_id) {
      const team = teams.find(t => t.id === feed.team_id);
      if (team) parts.push(team.name);
    } else {
      parts.push('All Teams');
    }
    
    if (feed.only_lineup_matches) {
      parts.push('Lineup Only');
    }
    
    return parts.join(' - ') || 'My Matches Feed';
  };

  const formatLastFetched = (lastFetched: string | null): string => {
    if (!lastFetched) return 'Never synced';
    
    const date = new Date(lastFetched);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse flex items-center justify-between mb-2">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-2/3"></div>
          <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-lg w-28"></div>
        </div>
        {[1, 2].map((i) => (
          <div key={i} className="animate-pulse border border-gray-100 dark:border-gray-700/50 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg flex-shrink-0"></div>
              <div className="flex-1 space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full w-1/3"></div>
                <div className="flex gap-2">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-20"></div>
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-24"></div>
                </div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-full"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start sm:items-center justify-between gap-4 mb-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Subscribe to your matches in Google Calendar, Apple Calendar, Outlook, or any app that supports ICS feeds.
        </p>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors text-sm font-medium min-h-[44px] flex-shrink-0 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Create Feed
        </button>
      </div>

      {feeds.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-14 h-14 bg-gray-100 dark:bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Rss className="w-7 h-7 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">No calendar feeds yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Create a feed to sync your matches with your favorite calendar app.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors text-sm font-medium shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Create Your First Feed
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {feeds.map((feed) => {
            const feedUrl = getFeedUrl(feed.token);
            const team = feed.team_id ? teams.find(t => t.id === feed.team_id) : null;
            const isCopied = copiedFeedId === feed.id;

            return (
              <div key={feed.id} className="border border-gray-100 dark:border-gray-700/50 rounded-xl p-4 sm:p-5 hover:shadow-sm transition-all group">
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Feed icon - hidden on small screens */}
                  <div className="hidden sm:flex w-10 h-10 bg-primary-50 dark:bg-primary-900/20 rounded-lg items-center justify-center flex-shrink-0">
                    <Rss className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Header row with name and actions */}
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {getFeedDisplayName(feed)}
                      </h3>
                      <div className="flex items-center gap-1 flex-shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEditModal(feed)}
                          className="p-2 sm:p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Edit feed"
                        >
                          <Edit2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                        </button>
                        <button
                          onClick={() => handleRegenerateToken(feed.id)}
                          className="p-2 sm:p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Regenerate token"
                        >
                          <RefreshCw className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteFeed(feed.id)}
                          className="p-2 sm:p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete feed"
                        >
                          <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Metadata badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {team ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300">
                          <Users className="w-3 h-3" />
                          {team.name}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400">
                          <Users className="w-3 h-3" />
                          All Teams
                        </span>
                      )}
                      {feed.only_lineup_matches && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">
                          <Filter className="w-3 h-3" />
                          Lineup Only
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3" />
                        {formatLastFetched(feed.last_fetched_at)}
                      </span>
                    </div>

                    {/* URL copy area */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 px-3 py-2 overflow-hidden">
                        <input
                          type="text"
                          value={feedUrl}
                          readOnly
                          className="w-full bg-transparent text-xs font-mono text-gray-600 dark:text-gray-400 focus:outline-none truncate"
                        />
                      </div>
                      <button
                        onClick={() => copyToClipboard(feedUrl, feed.id)}
                        className={`px-3 py-2 text-sm font-medium transition-all flex items-center gap-1.5 rounded-lg min-h-[36px] flex-shrink-0 ${
                          isCopied
                            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                        }`}
                        title="Copy URL"
                      >
                        {isCopied ? (
                          <>
                            <Check className="w-3.5 h-3.5" />
                            <span className="text-xs">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" />
                            <span className="text-xs">Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Instructions - Collapsible */}
      <div className="mt-6">
        <button
          onClick={() => setShowInstructions(!showInstructions)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
        >
          <div className="flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">How to subscribe in your calendar app</span>
          </div>
          {showInstructions ? (
            <ChevronUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          )}
        </button>
        {showInstructions && (
          <div className="mt-2 px-4 py-4 rounded-xl border border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Copy the calendar feed URL above, then add it to your calendar app:</p>
            <div className="space-y-2.5 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-1.5 h-1.5 bg-primary-500 dark:bg-primary-400 rounded-full mt-1.5"></span>
                <span><strong className="text-gray-900 dark:text-gray-100">Google Calendar</strong> — Settings → Add calendar → From URL, paste the URL</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-1.5 h-1.5 bg-primary-500 dark:bg-primary-400 rounded-full mt-1.5"></span>
                <span><strong className="text-gray-900 dark:text-gray-100">Apple Calendar</strong> — File → New Calendar Subscription, paste the URL</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-1.5 h-1.5 bg-primary-500 dark:bg-primary-400 rounded-full mt-1.5"></span>
                <span><strong className="text-gray-900 dark:text-gray-100">Outlook</strong> — Add calendar → Subscribe from web, paste the URL</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingFeed) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-100 dark:border-gray-700/50">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                    <Rss className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {editingFeed ? 'Edit Calendar Feed' : 'Create Calendar Feed'}
                  </h3>
                </div>
                <button
                  onClick={closeModals}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label htmlFor="feed-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Feed Name <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="feed-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., My Team Matches"
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  />
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Leave blank for an auto-generated name</p>
                </div>

                <div>
                  <label htmlFor="team-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Team Filter
                  </label>
                  <select
                    id="team-filter"
                    value={formData.team_id}
                    onChange={(e) => setFormData({ ...formData, team_id: e.target.value === 'all' ? 'all' : parseInt(e.target.value) })}
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
                  >
                    <option value="all">All Teams</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/50">
                  <input
                    type="checkbox"
                    id="lineup-only"
                    checked={formData.only_lineup_matches}
                    onChange={(e) => setFormData({ ...formData, only_lineup_matches: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-200 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="lineup-only" className="text-sm text-gray-700 dark:text-gray-300">
                    Only show matches where I'm in the lineup
                  </label>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={editingFeed ? handleUpdateFeed : handleCreateFeed}
                    className="flex-1 px-4 py-2.5 bg-primary-600 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-medium min-h-[44px] shadow-sm"
                  >
                    {editingFeed ? 'Update Feed' : 'Create Feed'}
                  </button>
                  <button
                    onClick={closeModals}
                    className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium min-h-[44px]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarFeed;

